import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import {
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  DialogActions
} from '@mui/material';
import { query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { mediasRef } from '../../api/firebase/firestore/media';
import {
  negotiationsRef,
  sendIssueLike
} from '../../api/firebase/firestore/negotiation';
import { issueLikeSchema } from '../../api/firebase/firestore/negotiation/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import {
  blackText,
  blue,
  gray,
  primaryButtonBackground
} from '../../constants/Colors';
import { buttonFont, titleMb } from '../../constants/Location';
import { status } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';

import CustomSelectField from './CustomSelectField';
import CustomTextfield from './CustomTextfield';

const styles = {
  buttonFont,
  complete: {
    '@media screen and (max-width:700px)': {
      padding: '24px 16px'
    },
    boxSizing: 'border-box',
    padding: '24px 40px',
    width: '100%'
  },
  completeButtonWrapper: {
    columnGap: '15px',
    display: 'flexbox',
    justifyContent: 'space-between'
  },
  completeCheckIcon: {
    color: blue,
    height: '56px',
    margin: '0 auto',
    width: '56px'
  },
  completeClose: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  completeCloseButton: {
    ':hover': {
      bgcolor: 'transparent',
      boxShadow: 'none'
    },
    '@media screen and (max-width:960px)': {
      height: '42px',
      width: '96px'
    },
    bgcolor: 'white',
    border: `1px solid ${gray}`,
    boxShadow: 'none',
    height: '42px',
    width: '124px'
  },
  completeDesc: {
    fontSize: '22px'
  },
  completeWrapper: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        '@media screen and (max-width:700px)': {
          maxWidth: '343px'
        },
        margin: 0,
        maxWidth: '676px'
      },
      '@media screen and (max-width:700px)': {
        width: '343px'
      },
      margin: '0 auto',
      width: '676px'
    }
  },
  confirmButton: {
    '@media screen and (max-width:960px)': {
      height: '42px',
      width: '199px'
    },
    height: '42px',
    width: '260px'
  },
  generateCheck: {
    '@media screen and (min-width:480px)': { display: 'none' }
  },
  guideCard: {
    '@media screen and (max-width:600px)': {
      width: '80%'
    },
    width: '480px'
  },
  guideCloseButton: {
    color: blue
  },
  guideMark: {
    color: gray,
    cursor: 'pointer',
    heihgt: '18px',
    ml: '8px',
    mt: '6px',
    width: '18px'
  },
  guideText: {
    color: blackText,
    fontSize: '14px',
    whiteSpace: 'pre-wrap'
  },
  guideTitle: {
    color: blackText,
    fontSize: '22px',
    marginBottom: titleMb,
    padding: 0
  },
  likeButton: {
    boxSizing: 'border-box',
    height: {
      xs: '36px'
    },
    width: {
      md: '200px',
      xs: '163px'
    }
  },
  likeCard: {
    '.MuiDialog-paper': {
      '@media screen and (max-width:480px)': {
        margin: 0,
        width: '90%'
      },
      maxWidth: '480px',
      width: '100%'
    }
  },
  modalButton: {
    marginTop: '24px'
  }
};

interface Props {
  issueId: string;
  cantInquiry: string | undefined;
  setOpen: () => void;
}

const PublisherLikeButton: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { issueId, cantInquiry, setOpen } = props;

  // いいねモーダル
  const [likeModalOpen, setLikeModalOpen] = React.useState(false);

  const handleLikeModalOpen = (): void => {
    setLikeModalOpen(!likeModalOpen);
  };

  interface LikeType {
    media: string;
    message: string;
  }

  // いいね送信
  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<LikeType>({
    resolver: yupResolver(issueLikeSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('media') === undefined ||
        0 ||
        watch('message') === undefined ||
        '' ||
        Boolean(errors.media) ||
        Boolean(errors.message)
      );
    }
    return false;
  };

  const [firstSubmit, setFirstSubmit] = useState<boolean>(true);
  const [submitted, setSubmitted] = useState(false);
  const [negotiationId, setNegotiationId] = useState('');

  const onSubmit = async (): Promise<void> => {
    if (currentUser && issueId) {
      const negId = await sendIssueLike(currentUser.uid, watch, issueId);
      setSubmitted(true);
      setNegotiationId(negId);
      handleLikeModalOpen();
      handleCompleteOpen();
      setSubmitted(false);
    }
  };

  // いいね送信用メディアリストの作成
  const [mediaList, setMediaList] = useState<
    Array<{ disabled: boolean; value: string; option: string }>
  >([]);

  useEffect(() => {
    void (async () => {
      if (currentUser && issueId) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        const userCompanyId = userData?.companyId as string;
        const q = query(
          mediasRef,
          where('companyId', '==', userCompanyId),
          where('status', '==', status.approved)
        );
        const querySnapshot = await getDocs(q);
        const list: Array<{
          disabled: boolean;
          value: string;
          option: string;
        }> = [];

        // すでに商談を進めているmediaは選択不可にする
        const matchedMediaIds: string[] = [];
        const negotiationQuery = query(
          negotiationsRef,
          where('issueId', '==', issueId)
        );
        const negotiationSnap = await getDocs(negotiationQuery);

        negotiationSnap.forEach((document) => {
          matchedMediaIds.push(document.data()?.mediaId as string);
        });

        querySnapshot.forEach((docs) => {
          const mediaId = docs.data().mediaId as string;
          const isMatched = matchedMediaIds.includes(mediaId);
          const mediaData = {
            disabled: isMatched,
            option: String(docs.data().name),
            value: mediaId
          };
          list.push(mediaData);
        });
        setMediaList(list);
      }
    })();
  }, [issueId, submitted]);

  // ガイドダイアログ
  const [guideOpen, setGuideOpen] = useState<boolean>(false);

  const handleGuideOpen = (): void => {
    setGuideOpen(!guideOpen);
  };

  // 送信完了ダイアログ
  const [completeOpen, setCompleteOpen] = useState<boolean>(false);

  const handleCompleteOpen = (): void => {
    setCompleteOpen(!completeOpen);
  };

  return (
    <>
      <Button
        variant="contained"
        sx={[primaryButtonBackground, styles.likeButton]}
        fullWidth
        onClick={(e) => {
          e.stopPropagation();
          if (cantInquiry) {
            setOpen();
          } else {
            handleLikeModalOpen();
          }
        }}
      >
        {t('given.likeIssue')}
      </Button>

      {/* いいね送信ダイアログ */}
      <Dialog
        open={likeModalOpen}
        onClose={handleLikeModalOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={styles.likeCard}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogTitle
            id="alert-dialog-title"
            sx={{
              '@media screen and (max-width:480px)': {
                padding: '24px 16px 16px'
              }
            }}
          >
            <Typography sx={{ display: 'flex' }}>
              <Typography sx={{ fontSize: '22px' }}>
                {t('issueFavoLike.sendLike')}
              </Typography>
              <HelpOutlineOutlinedIcon
                sx={styles.guideMark}
                onClick={() => handleGuideOpen()}
              />
              <div style={{ marginLeft: 'auto' }}>
                <IconButton onClick={handleLikeModalOpen} sx={{ padding: 0 }}>
                  <CloseIcon sx={{ color: gray }} />
                </IconButton>
              </div>
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{
              '@media screen and (max-width:480px)': {
                padding: '0 16px 24px'
              }
            }}
          >
            <DialogContentText id="alert-dialog-description">
              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    labelText={t('issueFavoLike.media')}
                    placeholder={t('form.selectOther')}
                    requiredText={t('form.required')}
                    error={Boolean(errors.media)}
                    helperText={errors.media?.message}
                    isRequired
                    defaultValue={0}
                    list={mediaList}
                    width={'search'}
                    blank
                  />
                )}
                control={control}
                name="media"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('issueFavoLike.message')}
                    requiredText={t('form.required')}
                    error={Boolean(errors.message)}
                    helperText={errors.message?.message}
                    isRequired
                    multiline
                    rows={7}
                    fieldType="textareaRow7"
                    placeholder={t('issueFavoLike.text')}
                    width={'contactDrawer'}
                  />
                )}
                control={control}
                name="message"
              />
            </DialogContentText>
            <Typography component="div" sx={styles.modalButton}>
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                color="error"
                fullWidth
                sx={[
                  primaryButtonBackground,
                  {
                    '@media screen and (max-width:960px)': { height: '42px' },
                    maxWidth: '100%'
                  }
                ]}
                onClick={async () => {
                  setFirstSubmit(true);
                  await handleSubmit(onSubmit)();
                }}
              >
                {t('issueFavoLike.send')}
              </Button>
            </Typography>
          </DialogContent>
        </div>
      </Dialog>

      {/* 与件いいねに関するガイドダイアログ */}
      <Dialog open={guideOpen} onClose={handleGuideOpen}>
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogContent sx={styles.guideCard}>
            <DialogTitle sx={styles.guideTitle}>
              {t('issueFavoLike.guideTitle')}
            </DialogTitle>
            <DialogContentText sx={styles.guideText}>
              {t('issueFavoLike.guideText')}
            </DialogContentText>
            <DialogActions>
              <Button onClick={handleGuideOpen} sx={styles.guideCloseButton}>
                {t('form.guideClose')}
              </Button>
            </DialogActions>
          </DialogContent>
        </div>
      </Dialog>

      {/* 送信完了ダイアログ */}
      <Dialog open={completeOpen} sx={styles.completeWrapper}>
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogContent sx={styles.complete}>
            <Typography component="div" sx={styles.completeClose}>
              <CloseIcon
                onClick={() => {
                  handleCompleteOpen();
                  setFirstSubmit(false);
                  reset();
                }}
                sx={{ cursor: 'pointer' }}
              />
            </Typography>
            <Grid container justifyContent={'center'}>
              <CheckCircleIcon sx={styles.completeCheckIcon} />
            </Grid>
            <Grid container justifyContent="center" sx={{ margin: '24px 0' }}>
              <Typography component="p" sx={styles.completeDesc}>
                {t('inquiry.complete')}
              </Typography>
            </Grid>
            <Grid
              container
              direction="row"
              alignItems="center"
              sx={styles.completeButtonWrapper}
            >
              <Button
                variant="contained"
                sx={styles.completeCloseButton}
                onClick={() => {
                  handleCompleteOpen();
                  setFirstSubmit(false);
                  reset();
                }}
              >
                <Typography
                  component="div"
                  color={blackText}
                  sx={styles.buttonFont}
                >
                  {t('inquiry.close')}
                </Typography>
              </Button>
              <Button
                variant="contained"
                sx={[primaryButtonBackground, styles.confirmButton]}
                onClick={(
                  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  e.stopPropagation();
                  navigate(`/publisher/mypage/message/${negotiationId}`);
                }}
              >
                <Typography
                  component="div"
                  color="white"
                  sx={styles.buttonFont}
                >
                  {t('inquiry.confirm')}
                </Typography>
              </Button>
            </Grid>
          </DialogContent>
        </div>
      </Dialog>
    </>
  );
};

export default PublisherLikeButton;
